import { TFunction } from "i18next";
import * as yup from "yup";

import { ProgramType } from "../Program";
import { OtherEnrollmentConditionId } from "./EnrollmentCondition"
import { OtherFormOfTeachingId, OtherKnowledgeCheckId } from "./OtherDataValues";
import { ProgramRequestStatus } from "./ProgramRequestStatus";
import { TeacherValidationScheme } from "./TeacherValidationScheme";

export interface ProgramRequestDataValidationSchemeProps {
  t: TFunction;
}

export const ProgramRequestDataValidationScheme = ({
  t,
}: ProgramRequestDataValidationSchemeProps) => {
  const requiredMsg = t("validations.required");
  const requiredAtLeastOne = t("validations.requiredAtLeastOne");
  const maximumTenDocuments = t("validations.maximumTenDocuments");
  const maximumTenOtherLegalEntityLocations = t("validations.maximumTenOtherLegalEntityLocations");
  const requiredAtLeastOneTeacher = t("validations.requiredAtLeastOneTeacher");

  const executionResolutionDocumentMinDate = new Date(2022, 0, 1);
  const expertOpinionDocumentMinDateMsg = t("request.expertOpinionDocumentMinDate");
  const levelValidation = t("request.levelValidation");
  const csvetValidation = t("request.csvetValidation");
  const remarkValidation = t("request.remarkValidation");

  const executionResolutionDocumentMaxDate = (): Date => {
    const now = new Date();
    const todayLastSecond = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    return todayLastSecond;
  };
  const executionResolutionDocumentMaxDateMsg = t("request.executionResolutionDocumentMaxDate");
  const positiveNumberOrZeroMsg = t("validations.positiveNumberOrZero");
  const numberLessThanTousend = t("validations.numberLessThan", { nr: 6000 });
  const documentTooBig = t("errorCode.Document.TooBig");
  const documentWrongFormat = t("errorCode.Document.WrongFormatPdfWord");
  const documentWrongFormatImg = t("errorCode.Document.WrongFormat");
  const requiresTeacherForMzoPhase = (
    programTypeId: number,
    statusId: ProgramRequestStatus,
  ): boolean => {
    if (programTypeId === ProgramType.ProgramWithMinisterResolution) {
      return statusId === ProgramRequestStatus.SentMZO ||
        statusId === ProgramRequestStatus.SubmittedMZO ||
        statusId === ProgramRequestStatus.UnlockedMZO;
    }

    return statusId === ProgramRequestStatus.Evaluated ||
      statusId === ProgramRequestStatus.SentMZO ||
      statusId === ProgramRequestStatus.UnlockedMZO;
  };

  return yup.object().shape({
    programTypeId: yup.number().required(requiredMsg).test('programTypeId', requiredMsg, val => {
      if (val && val !== 0) {
        return true;
      }
      return false;
    }),
    programId: yup.number().nullable().required(requiredMsg),
    hkoLevelId: yup
      .number()
      .nullable()
      .transform((value, originalValue) => {
        if (originalValue === "" || Number.isNaN(value)) {
          return null;
        }
        return value;
      })
      .when("programTypeId", {
        is: ProgramType.TrainingPrograms,
        then: yup.number().nullable().required(requiredMsg).min(1, requiredMsg).typeError(requiredMsg),
        otherwise: yup.number().nullable(),
      }),
    qualificationTypeId: yup.number().when("programId", {
      is: -1,
      then: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).typeError(requiredMsg),
      otherwise: yup.number().nullable()
    }),
    curriculumProgramDocument: yup.mixed().required(requiredMsg).test('curriculumProgramDocument', documentTooBig, val => {
      if (val && val.id !== -1) {
        return true;
      }
      if (val && val.file.size <= 10485760) {
        return true;
      }
      return false;
    }).test('curriculumProgramDocument', documentWrongFormat, val => {
      if (val) {
        var split = val.fileName.split('.');
        var ext = split[split.length - 1];

        if (ext === "doc" || ext === "docx" || ext === "pdf") {
          return true;
        }
      }
      return false;
    }),
    guidedLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend).typeError(requiredMsg),
    independentLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend).typeError(requiredMsg),
    workLearningDuration: yup.number().required(requiredMsg).min(0, positiveNumberOrZeroMsg).max(6000, numberLessThanTousend).typeError(requiredMsg),
    formOfTeachingIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    knowledgeChecksIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    enrollmentConditionsIds: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
    otherEnrollmentConditions: yup.array().of(yup.string()).when("enrollmentConditionsIds", (enrollmentConditionsIds, schema) => {
      if (enrollmentConditionsIds.includes(OtherEnrollmentConditionId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      } else {
        return schema;
      }
    }),
    otherFormsOfTeaching: yup.array().of(yup.string()).when("formOfTeachingIds", (formOfTeachingIds, schema) => {
      if (formOfTeachingIds.includes(OtherFormOfTeachingId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      }
      else {
        return schema;
      }
    }),
    otherKnowledgeChecks: yup.array().of(yup.string()).when("knowledgeChecksIds", (knowledgeChecksIds, schema) => {
      if (knowledgeChecksIds.includes(OtherKnowledgeCheckId)) {
        return yup.array().of(yup.string().required(requiredMsg));
      }
      else {
        return schema;
      }
    }),
    // Validate competence names only for types that capture them (the types listed below are skipped).
    // The .when must be at array level: inside .of() programTypeId was undefined, so names were
    // required everywhere and names with stray whitespace failed .trim() and blocked submit.
    competences: yup.array().of(
      yup.object().shape({ name: yup.string().trim().required(requiredMsg).max(512, t("validations.maximalStringLength", { maxLength: 512 })) })
    ).when("programTypeId", (programTypeId, schema) => {
      if (programTypeId === ProgramType.BasicEducationPrograms ||
        programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
        programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
        programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
        programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
        programTypeId === ProgramType.BasicArtsPrograms ||
        programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
        programTypeId === ProgramType.CroatianLanguageForForeigners) {
        return yup.array().of(yup.object().nullable());
      }
      return schema;
    }),
    programName: yup.string().when("programId", {
      is: -1,
      then: yup.string().nullable().required(requiredMsg).max(512, t("validations.maximalStringLength", { maxLength: 512 })),
      otherwise: yup.string().nullable()
    }),
    remarkInstitution: yup.string().nullable().notRequired().test('remarkInstitution', remarkValidation, val => {
      if (val) {
        console.log('remarkInstitution ' + val.length);
        return val.length < 701;
      }
      return true;
    }),
    modules: yup.array().of(
      yup.object().shape(
        {
          name: yup.string().required(requiredMsg).max(255, t("validations.maximalStringLength", { maxLength: 255 })),
          learningSets: yup.array().of(
            yup.object().shape(
              {
                name: yup.string().required(requiredMsg).max(255, t("validations.maximalStringLength", { maxLength: 255 })),
                level: yup.number().required(requiredMsg).min(2, levelValidation).max(6, levelValidation).typeError(requiredMsg),
                csvet: yup.number().required(requiredMsg).min(1, csvetValidation)
              }
            )
          ).min(1, requiredMsg)
        }
      )
    ).when("programTypeId", (programTypeId, schema) => {
      if (programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
        programTypeId === ProgramType.CraftsSpecializationPrograms ||
        programTypeId === ProgramType.TrainingPrograms ||
        programTypeId === ProgramType.RefinementPrograms ||        
        programTypeId === ProgramType.NewVocationalCurriculum) {
        return schema.min(1, requiredMsg);
      } else {
        return schema;
      }
    }),
    teachers: yup.array().of(
      TeacherValidationScheme({ t })
    ).when(["programTypeId", "statusId"], {
      is: requiresTeacherForMzoPhase,
      then: (schema) => schema.min(1, requiredAtLeastOneTeacher),
      otherwise: (schema) => schema,
    }),
    languageLevelGeneralIds: yup.array().of(yup.number()),
    languageLevelBusinessIds: yup.array().of(yup.number()),    
    educationSubSectorId: yup.number().nullable().when(["programId", "qualificationTypeId"], {
      is: (programId: number, qualificationTypeId: number) => programId === -1 && qualificationTypeId !== 4,
        then: yup.number().nullable().required(requiredMsg),
      otherwise: yup.number().nullable()
    }),
    locationOutsideHeadquartersIds: yup.array().of(yup.number()).when("locationOutsideHeadquarters", {
      is: true,
      then: yup.array().of(yup.number()).min(1, requiredAtLeastOne),
      otherwise: yup.array().of(yup.number())
    }),
    //agencyExpertOpinionDocuments: yup.array().of(
    //  yup.object().shape({
    //    classificationMark: yup.string().required(requiredMsg),
    //    registryNumber: yup.string().required(requiredMsg),
    //    issueDate:
    //      yup.date().when("programTypeId", (programTypeId, schema) => {
    //        if (programTypeId === ProgramType.BasicEducationPrograms ||
    //          programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
    //          programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
    //          programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
    //          programTypeId === ProgramType.ForeignLanguageLearningPrograms) {
    //          return schema;
    //        } else {
    //          return yup.date()
    //            .required(requiredMsg)
    //            .min(executionResolutionDocumentMinDate, expertOpinionDocumentMinDateMsg)
    //            .max(executionResolutionDocumentMaxDate(), executionResolutionDocumentMaxDateMsg)
    //            .typeError(requiredMsg)
    //        }
    //      }),
    //    document: yup.object().required(requiredMsg).typeError(requiredMsg)
    //  }).typeError(requiredMsg)
    //).min(1, requiredAtLeastOne).max(10, maximumTenDocuments),
    //executionResolutionDocuments: yup.array().of(
    //  yup.object().shape({
    //    classificationMark: yup.string().required(requiredMsg),
    //    registryNumber: yup.string().required(requiredMsg),
    //    issueDate:
    //      yup.date().when("programTypeId", (programTypeId, schema) => {
    //        if (programTypeId === ProgramType.BasicEducationPrograms ||
    //          programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
    //          programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
    //          programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
    //          programTypeId === ProgramType.ForeignLanguageLearningPrograms) {
    //          return schema;
    //        } else {
    //          return yup.date()
    //            .required(requiredMsg)
    //            .min(executionResolutionDocumentMinDate, expertOpinionDocumentMinDateMsg)
    //            .max(executionResolutionDocumentMaxDate(), executionResolutionDocumentMaxDateMsg)
    //            .typeError(requiredMsg)
    //        }
    //      }),
    //    document: yup.object().required(requiredMsg).typeError(requiredMsg)
    //  }).typeError(requiredMsg)
    //).min(1, requiredAtLeastOne).max(10, maximumTenDocuments),
    otherLegalEntityLocations: yup.array().when("locationOtherLegalEntity", {
      is: true,
      then: yup.array().of(yup.object().shape({
        name: yup.string().required(requiredMsg),
        programExecutionRequestDocument: yup.mixed().required(requiredMsg).typeError(requiredMsg).test('programExecutionRequestDocument', documentTooBig, val => {
          if (val && val.id !== -1) {
            return true;
          }
          if (val && val.file.size <= 10485760) {
            return true;
          }
          return false;
        }).test('programExecutionRequestDocument', documentWrongFormatImg, val => {
          if (val) {
            var split = val.fileName.split('.');
            var ext = split[split.length - 1];

            if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
              return true;
            }
          }
          return false;
        }),
        ministryConsentDocument: yup.mixed().required(requiredMsg).typeError(requiredMsg).test('ministryConsentDocument', documentTooBig, val => {
          if (val && val.id !== -1) {
            return true;
          }
          if (val && val.file.size <= 10485760) {
            return true;
          }
          return false;
        }).test('ministryConsentDocument', documentWrongFormatImg, val => {
          if (val) {
            var split = val.fileName.split('.');
            var ext = split[split.length - 1];

            if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
              return true;
            }
          }
          return false;
        }),
        personnelConditionsDocument: yup.mixed().required(requiredMsg).typeError(requiredMsg).test('personnelConditionsDocument', documentTooBig, val => {
          if (val && val.id !== -1) {
            return true;
          }
          if (val && val.file.size <= 10485760) {
            return true;
          }
          return false;
        }).test('personnelConditionsDocument', documentWrongFormatImg, val => {
          if (val) {
            var split = val.fileName.split('.');
            var ext = split[split.length - 1];

            if (ext === "pdf" || ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif" || ext === "tiff" || ext === "bmp") {
              return true;
            }
          }
          return false;
        }),
        address: yup.string().required(requiredMsg),
        townId: yup.number().required(requiredMsg).typeError(requiredMsg)
      })).min(1, requiredAtLeastOne).max(10, maximumTenOtherLegalEntityLocations),
      otherwise: yup.array()
    })
  }).test("locationChosen", requiredAtLeastOne, value => {
    return value.locationOutsideHeadquarters || value.locationInsideHeadquarters || value.locationOtherLegalEntity
  }).test("languageLevelChosen", requiredAtLeastOne, value => {
    return ((value.programTypeId !== ProgramType.ForeignLanguageLearningPrograms && value.programTypeId !== ProgramType.CroatianLanguageForForeigners) ||
      (value.languageLevelGeneralIds !== undefined && value.languageLevelGeneralIds.length > 0) ||
      (value.languageLevelBusinessIds !== undefined && value.languageLevelBusinessIds.length > 0))
  }).test("durationChosen", requiredAtLeastOne, value => {
    return ((value.guidedLearningDuration !== undefined && value.guidedLearningDuration > 0) ||
      (value.independentLearningDuration !== undefined && value.independentLearningDuration > 0) ||
      (value.workLearningDuration !== undefined && value.workLearningDuration > 0))
  });
};